import React from 'react';
import { CustomHeader } from '../../../shared/types/mcp';

interface CustomHeadersProps {
  headers: CustomHeader[];
  onChange: (headers: CustomHeader[]) => void;
}

export const CustomHeaders: React.FC<CustomHeadersProps> = ({ headers, onChange }) => {
  const addHeader = () => {
    const newHeader: CustomHeader = {
      id: `header-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: '',
      value: '',
      enabled: true,
    };
    onChange([...headers, newHeader]);
  };

  const updateHeader = (id: string, updates: Partial<CustomHeader>) => {
    onChange(
      headers.map(header =>
        header.id === id ? { ...header, ...updates } : header
      )
    );
  };

  const removeHeader = (id: string) => {
    onChange(headers.filter(header => header.id !== id));
  };

  const toggleHeader = (id: string) => {
    updateHeader(id, { enabled: !headers.find(h => h.id === id)?.enabled });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-300">Custom Headers</h4>
        <button
          onClick={addHeader}
          className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded"
        >
          + Add Header
        </button>
      </div>

      {headers.length === 0 ? (
        <div className="text-center py-4 text-gray-400 text-sm">
          No custom headers configured
        </div>
      ) : (
        <div className="space-y-3">
          {headers.map((header) => (
            <div key={header.id} className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={header.enabled}
                    onChange={() => toggleHeader(header.id)}
                    className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                  />
                  <span className={`text-sm font-medium ${header.enabled ? 'text-white' : 'text-gray-400'}`}>
                    {header.name || 'Unnamed Header'}
                  </span>
                </div>
                <button
                  onClick={() => removeHeader(header.id)}
                  className="text-red-400 hover:text-red-300 text-sm"
                >
                  Remove
                </button>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Header Name</label>
                  <input
                    type="text"
                    value={header.name}
                    onChange={(e) => updateHeader(header.id, { name: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                    placeholder="X-API-Key"
                    disabled={!header.enabled}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Header Value</label>
                  <input
                    type="text"
                    value={header.value}
                    onChange={(e) => updateHeader(header.id, { value: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                    placeholder="your-api-key-value"
                    disabled={!header.enabled}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="text-xs text-gray-500 mt-2">
        <p>Custom headers will be sent with every request to this MCP server.</p>
        <p>Common headers: Authorization, X-API-Key, Content-Type, User-Agent</p>
      </div>
    </div>
  );
};
